// src/components/templates/diary/CourseCreationModal.jsx
import React, { useState, useEffect } from 'react';
import {
  X, Plus, Trash2, BookOpen, FileText, Video, Image, HelpCircle,
  ChevronDown, ChevronUp, GripVertical, Save, Eye
} from 'lucide-react';
import { CourseCategory, CourseDifficulty, BlockType, AssessmentType } from '../../constants/learningConstants';
import { firestoreService } from '../../services/firestoreService';

const CourseCreationModal = ({ bookId, user, onClose, onSave, onUpdateCourse, onSaveLesson, diaryService, editCourse = null }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!editCourse);

  const isEditMode = !!editCourse;

  // Course Basic Info
  const [courseData, setCourseData] = useState({
    title: editCourse?.title || '',
    description: editCourse?.description || '',
    instructor: editCourse?.instructor || user?.displayName || '',
    category: editCourse?.category || CourseCategory.TECHNOLOGY,
    difficulty: editCourse?.difficulty || CourseDifficulty.BEGINNER,
    isFree: editCourse?.isFree ?? true,
    price: editCourse?.price || 0,
    currency: editCourse?.currency || 'USD',
    language: editCourse?.language || 'English',
    thumbnail: editCourse?.thumbnail || null,
    learningObjectives: editCourse?.learningObjectives || [''],
    competencies: editCourse?.competencies || [],
    tags: editCourse?.tags || []
  });

  // Lessons
  const [lessons, setLessons] = useState([
    {
      title: '',
      description: '',
      duration: 0,
      order: 0,
      contentBlocks: [],
      isPreview: false
    }
  ]);

  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);

  // Load existing lessons if editing
  useEffect(() => {
    const loadExistingLessons = async () => {
      if (!editCourse || !diaryService) return;
      
      setLoading(true);
      try {
        const existingLessons = await diaryService.getLessons(bookId, editCourse.id);
        if (existingLessons.length > 0) {
          setLessons(existingLessons);
        }
      } catch (error) {
        console.error('Error loading lessons:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExistingLessons();
  }, [editCourse, bookId, diaryService]);

  const handleCourseDataChange = (field, value) => {
    setCourseData(prev => ({ ...prev, [field]: value }));
  };

  const handleObjectiveChange = (index, value) => {
    const updated = [...courseData.learningObjectives];
    updated[index] = value;
    setCourseData(prev => ({ ...prev, learningObjectives: updated }));
  };

  const addObjective = () => {
    setCourseData(prev => ({
      ...prev,
      learningObjectives: [...prev.learningObjectives, '']
    }));
  };

  const removeObjective = (index) => {
    const updated = courseData.learningObjectives.filter((_, i) => i !== index);
    setCourseData(prev => ({ ...prev, learningObjectives: updated }));
  };

  const addLesson = () => {
    setLessons(prev => [...prev, {
      title: '',
      description: '',
      duration: 0,
      order: prev.length,
      contentBlocks: [],
      isPreview: false
    }]);
    setCurrentLessonIndex(lessons.length);
  };

  const removeLesson = (index) => {
    setLessons(prev => prev.filter((_, i) => i !== index));
    if (currentLessonIndex >= index) {
      setCurrentLessonIndex(Math.max(0, currentLessonIndex - 1));
    }
  };

  const updateLesson = (index, field, value) => {
    setLessons(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addContentBlock = (lessonIndex, type) => {
    const newBlock = {
      type,
      order: lessons[lessonIndex].contentBlocks.length,
      content: type === BlockType.QUIZ ? {
        questions: [{
          type: AssessmentType.MULTIPLE_CHOICE,
          question: '',
          options: ['', '', '', ''],
          correctAnswer: 0,
          explanation: '',
          competencyId: null
        }]
      } : ''
    };

    setLessons(prev => {
      const updated = [...prev];
      updated[lessonIndex].contentBlocks.push(newBlock);
      return updated;
    });
  };

  const updateContentBlock = (lessonIndex, blockIndex, field, value) => {
    setLessons(prev => {
      const updated = [...prev];
      updated[lessonIndex].contentBlocks[blockIndex] = {
        ...updated[lessonIndex].contentBlocks[blockIndex],
        [field]: value
      };
      return updated;
    });
  };

  const removeContentBlock = (lessonIndex, blockIndex) => {
    setLessons(prev => {
      const updated = [...prev];
      updated[lessonIndex].contentBlocks = updated[lessonIndex].contentBlocks.filter((_, i) => i !== blockIndex);
      return updated;
    });
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const url = await firestoreService.uploadFile(file, `pb-${bookId}/courses/thumbnails`);
      setCourseData(prev => ({ ...prev, thumbnail: url }));
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      alert('Failed to upload thumbnail');
    }
  };

  const handleSave = async () => {
    if (!courseData.title || lessons.length === 0) {
      alert('Please provide a course title and at least one lesson');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        // EDIT MODE: Update existing course
        const updatedCourse = {
          ...editCourse,
          ...courseData,
          lessonCount: lessons.length,
          estimatedDuration: lessons.reduce((sum, l) => sum + (l.duration || 0), 0)
        };

        await onUpdateCourse(updatedCourse);

        // Update all lessons (delete removed, add new, update existing)
        for (const lesson of lessons) {
          await onSaveLesson(editCourse.id, lesson);
        }

      } else {
        // CREATE MODE: Three-step process
        // Step 1: Create course as DRAFT
        const newCourse = {
          ...courseData,
          isPublished: false,  // Save as draft first
          lessonCount: lessons.length,
          estimatedDuration: lessons.reduce((sum, l) => sum + (l.duration || 0), 0),
          enrollmentCount: 0,
          rating: 0
        };

        const courseId = await onSave(newCourse);

        // Step 2: Create all lessons with the courseId
        for (const lesson of lessons) {
          await onSaveLesson(courseId, lesson);
        }

        // Step 3: Update course to published status
        await onUpdateCourse({
          ...newCourse,
          id: courseId,
          isPublished: true
        });
      }

      onClose();
    } catch (error) {
      console.error('Error saving course:', error);
      alert('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const currentLesson = lessons[currentLessonIndex];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Course' : 'Create Course'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-4">
            <StepButton number={1} label="Course Info" active={step === 1} onClick={() => setStep(1)} />
            <div className="h-px flex-1 bg-gray-300" />
            <StepButton number={2} label="Lessons" active={step === 2} onClick={() => setStep(2)} />
            <div className="h-px flex-1 bg-gray-300" />
            <StepButton number={3} label="Review" active={step === 3} onClick={() => setStep(3)} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Course Title *</label>
                <input
                  type="text"
                  value={courseData.title}
                  onChange={(e) => handleCourseDataChange('title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Introduction to Web Development"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={courseData.description}
                  onChange={(e) => handleCourseDataChange('description', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Describe what students will learn in this course..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instructor</label>
                  <input
                    type="text"
                    value={courseData.instructor}
                    onChange={(e) => handleCourseDataChange('instructor', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={courseData.category}
                    onChange={(e) => handleCourseDataChange('category', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    {Object.entries(CourseCategory).map(([key, value]) => (
                      <option key={value} value={value}>
                        {key.charAt(0) + key.slice(1).toLowerCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                  <select
                    value={courseData.difficulty}
                    onChange={(e) => handleCourseDataChange('difficulty', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    {Object.entries(CourseDifficulty).map(([key, value]) => (
                      <option key={value} value={value}>
                        {key.charAt(0) + key.slice(1).toLowerCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                  <input
                    type="text"
                    value={courseData.language}
                    onChange={(e) => handleCourseDataChange('language', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Pricing */}
              <div>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={courseData.isFree}
                    onChange={(e) => handleCourseDataChange('isFree', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Free Course</span>
                </label>
                {!courseData.isFree && (
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="number"
                      value={courseData.price}
                      onChange={(e) => handleCourseDataChange('price', parseFloat(e.target.value))}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Price"
                    />
                    <select
                      value={courseData.currency}
                      onChange={(e) => handleCourseDataChange('currency', e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="ZAR">ZAR</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Thumbnail */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Course Thumbnail</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                {courseData.thumbnail && (
                  <img src={courseData.thumbnail} alt="Thumbnail" className="mt-2 h-32 rounded-lg object-cover" />
                )}
              </div>

              {/* Learning Objectives */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Learning Objectives</label>
                <div className="space-y-2">
                  {courseData.learningObjectives.map((obj, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={obj}
                        onChange={(e) => handleObjectiveChange(index, e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="What will students learn?"
                      />
                      <button
                        onClick={() => removeObjective(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addObjective}
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add Objective
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {/* Lesson Selector */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {lessons.map((lesson, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentLessonIndex(index)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap shrink-0 ${
                      index === currentLessonIndex
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Lesson {index + 1}
                  </button>
                ))}
                <button
                  onClick={addLesson}
                  className="px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 shrink-0"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Current Lesson Editor */}
              {currentLesson && (
                <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Lesson {currentLessonIndex + 1}</h3>
                    <button
                      onClick={() => removeLesson(currentLessonIndex)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Lesson Title</label>
                    <input
                      type="text"
                      value={currentLesson.title}
                      onChange={(e) => updateLesson(currentLessonIndex, 'title', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Getting Started with HTML"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={currentLesson.description}
                      onChange={(e) => updateLesson(currentLessonIndex, 'description', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
                    <input
                      type="number"
                      value={currentLesson.duration}
                      onChange={(e) => updateLesson(currentLessonIndex, 'duration', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Content Blocks */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-medium text-gray-700">Content Blocks</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => addContentBlock(currentLessonIndex, BlockType.TEXT)}
                          className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => addContentBlock(currentLessonIndex, BlockType.VIDEO)}
                          className="px-3 py-1 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 text-sm"
                        >
                          <Video className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => addContentBlock(currentLessonIndex, BlockType.QUIZ)}
                          className="px-3 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm"
                        >
                          <HelpCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {currentLesson.contentBlocks.map((block, blockIndex) => (
                        <ContentBlockEditor
                          key={blockIndex}
                          block={block}
                          blockIndex={blockIndex}
                          onUpdate={(field, value) => updateContentBlock(currentLessonIndex, blockIndex, field, value)}
                          onRemove={() => removeContentBlock(currentLessonIndex, blockIndex)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Review Your Course</h3>
                <p className="text-blue-700 text-sm">
                  Review all course details before publishing. You can edit after creation.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Course Information</h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                  <div><strong>Title:</strong> {courseData.title}</div>
                  <div><strong>Category:</strong> {courseData.category}</div>
                  <div><strong>Difficulty:</strong> {courseData.difficulty}</div>
                  <div><strong>Pricing:</strong> {courseData.isFree ? 'Free' : `$${courseData.price} ${courseData.currency}`}</div>
                  <div><strong>Lessons:</strong> {lessons.length}</div>
                  <div><strong>Objectives:</strong> {courseData.learningObjectives.filter(o => o).length}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <div className="flex gap-2">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Previous
              </button>
            )}
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Course')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== SUB-COMPONENTS ====================

const StepButton = ({ number, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
      active ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
      active ? 'bg-white text-indigo-600' : 'bg-gray-300 text-gray-700'
    }`}>
      {number}
    </div>
    <span className="font-medium">{label}</span>
  </button>
);

const ContentBlockEditor = ({ block, blockIndex, onUpdate, onRemove }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-900">
            {block.type === BlockType.TEXT && 'Text Block'}
            {block.type === BlockType.VIDEO && 'Video Block'}
            {block.type === BlockType.QUIZ && 'Quiz Block'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            onClick={onRemove}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-3">
          {block.type === BlockType.TEXT && (
            <textarea
              value={block.content}
              onChange={(e) => onUpdate('content', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Enter text content (supports markdown)..."
            />
          )}

          {block.type === BlockType.VIDEO && (
            <input
              type="text"
              value={block.content}
              onChange={(e) => onUpdate('content', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Video URL or embed code..."
            />
          )}

          {block.type === BlockType.QUIZ && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">Quiz questions will be configured in the lesson editor</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CourseCreationModal;